import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Image as ImageIcon, Plus, Send, Settings, Trash2, Upload,
  Loader2, ArrowLeft, Clock, Check, X, RefreshCw, Link2,
  DollarSign, Tag, ToggleLeft, ToggleRight, History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const InstagramPostPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('create');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Post form state
  const [imageUrls, setImageUrls] = useState(['']);
  const [caption, setCaption] = useState('');
  
  // Post history
  const [posts, setPosts] = useState([]);
  
  // Auto-post settings
  const [autoPostSettings, setAutoPostSettings] = useState({
    enabled: false,
    source: 'both',
    include_price: true,
    include_shop_link: true,
    shop_link_url: '',
    category_filter: [],
    caption_template: '{title}\n\n{price}\n\nShop now: {link}'
  });
  
  // Categories for filter
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load posts, settings, and categories in parallel
      const [postsRes, settingsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/instagram/posts`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/instagram/auto-post/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/instagram/product-categories`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPosts(postsRes.data.posts || []);
      setAutoPostSettings(settingsRes.data.settings || autoPostSettings);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async () => {
    const validUrls = imageUrls.filter(url => url.trim());
    
    if (validUrls.length === 0) {
      toast.error('Please add at least one image URL');
      return;
    }
    
    if (!caption.trim()) {
      toast.error('Please enter a caption');
      return;
    }
    
    if (caption.length > 2200) {
      toast.error('Caption cannot exceed 2200 characters');
      return;
    }

    setPosting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/instagram/post`,
        {
          image_urls: validUrls,
          caption: caption,
          is_carousel: validUrls.length > 1
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Posted to Instagram successfully!');
      setImageUrls(['']);
      setCaption('');
      loadData(); // Refresh post history
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to post to Instagram';
      toast.error(errorMsg);
    } finally {
      setPosting(false);
    }
  };

  const handleSaveAutoPostSettings = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/instagram/auto-post/settings`,
        autoPostSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Auto-post settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const addImageUrl = () => {
    if (imageUrls.length < 10) {
      setImageUrls([...imageUrls, '']);
    } else {
      toast.error('Maximum 10 images for carousel');
    }
  };

  const removeImageUrl = (index) => {
    if (imageUrls.length > 1) {
      setImageUrls(imageUrls.filter((_, i) => i !== index));
    }
  };

  const updateImageUrl = (index, value) => {
    const newUrls = [...imageUrls];
    newUrls[index] = value;
    setImageUrls(newUrls);
  };

  const toggleCategory = (category) => {
    setAutoPostSettings(prev => {
      const currentFilters = prev.category_filter || [];
      if (currentFilters.includes(category)) {
        return {
          ...prev,
          category_filter: currentFilters.filter(c => c !== category)
        };
      } else {
        return {
          ...prev,
          category_filter: [...currentFilters, category]
        };
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wt-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wt-bg p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/integrations/instagram')}
            className="text-wt-text-muted hover:text-wt-text-primary"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
            <ImageIcon className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-wt-text-primary">Instagram Posts</h1>
            <p className="text-wt-text-muted">Create posts and manage auto-posting</p>
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-wt-surface border border-wt-border">
            <TabsTrigger value="create" className="data-[state=active]:bg-wt-gold data-[state=active]:text-wt-bg">
              <Send className="h-4 w-4 mr-2" />
              Create Post
            </TabsTrigger>
            <TabsTrigger value="auto-post" className="data-[state=active]:bg-wt-gold data-[state=active]:text-wt-bg">
              <Settings className="h-4 w-4 mr-2" />
              Auto-Post Settings
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-wt-gold data-[state=active]:text-wt-bg">
              <History className="h-4 w-4 mr-2" />
              Post History
            </TabsTrigger>
          </TabsList>

          {/* Create Post Tab */}
          <TabsContent value="create">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-wt-surface border-wt-border">
                <CardHeader>
                  <CardTitle className="text-wt-text-primary">Create Instagram Post</CardTitle>
                  <CardDescription className="text-wt-text-muted">
                    Post a single image or carousel (up to 10 images)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Image URLs */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-wt-text-secondary">Image URLs</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={addImageUrl}
                        disabled={imageUrls.length >= 10}
                        className="border-wt-border text-wt-text-secondary hover:text-wt-text-primary"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Image
                      </Button>
                    </div>
                    
                    {imageUrls.map((url, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={url}
                          onChange={(e) => updateImageUrl(index, e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          className="bg-wt-bg border-wt-border text-wt-text-primary flex-1"
                        />
                        {imageUrls.length > 1 && (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => removeImageUrl(index)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    
                    <p className="text-xs text-wt-text-muted">
                      Images must be publicly accessible URLs (JPEG or PNG). {imageUrls.length > 1 && `Creating carousel with ${imageUrls.filter(u => u.trim()).length} images.`}
                    </p>
                  </div>

                  {/* Image Previews */}
                  {imageUrls.some(url => url.trim()) && (
                    <div className="flex gap-2 flex-wrap">
                      {imageUrls.filter(url => url.trim()).map((url, index) => (
                        <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden border border-wt-border">
                          <img 
                            src={url} 
                            alt={`Preview ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="%23333" width="80" height="80"/><text fill="%23999" x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="10">Error</text></svg>';
                            }}
                          />
                          <span className="absolute bottom-0 right-0 bg-black/70 text-white text-xs px-1">
                            {index + 1}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Caption */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-wt-text-secondary">Caption</Label>
                      <span className={`text-xs ${caption.length > 2200 ? 'text-red-400' : 'text-wt-text-muted'}`}>
                        {caption.length}/2200
                      </span>
                    </div>
                    <Textarea
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Write your caption here... Use line breaks for better readability."
                      className="bg-wt-bg border-wt-border text-wt-text-primary min-h-[150px]"
                    />
                  </div>

                  {/* Post Button */}
                  <Button
                    onClick={handlePost}
                    disabled={posting || !imageUrls.some(u => u.trim()) || !caption.trim()}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                  >
                    {posting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    {posting ? 'Posting...' : 'Post to Instagram'}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Auto-Post Settings Tab */}
          <TabsContent value="auto-post">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-wt-surface border-wt-border">
                <CardHeader>
                  <CardTitle className="text-wt-text-primary flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Auto-Post Settings
                  </CardTitle>
                  <CardDescription className="text-wt-text-muted">
                    Automatically post new products to Instagram
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-wt-bg rounded-lg">
                    <div className="flex items-center gap-3">
                      {autoPostSettings.enabled ? (
                        <ToggleRight className="h-6 w-6 text-green-400" />
                      ) : (
                        <ToggleLeft className="h-6 w-6 text-wt-text-muted" />
                      )}
                      <div>
                        <Label className="text-wt-text-primary font-medium">Auto-Post Enabled</Label>
                        <p className="text-xs text-wt-text-muted">
                          Automatically post when new products are added
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={autoPostSettings.enabled}
                      onCheckedChange={(checked) => setAutoPostSettings(prev => ({ ...prev, enabled: checked }))}
                    />
                  </div>

                  {/* Product Source */}
                  <div className="space-y-2">
                    <Label className="text-wt-text-secondary">Product Source</Label>
                    <Select
                      value={autoPostSettings.source}
                      onValueChange={(value) => setAutoPostSettings(prev => ({ ...prev, source: value }))}
                    >
                      <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-surface border-wt-border">
                        <SelectItem value="both" className="text-wt-text-primary">Both Shopify & Inventory</SelectItem>
                        <SelectItem value="shopify" className="text-wt-text-primary">Shopify Products Only</SelectItem>
                        <SelectItem value="inventory" className="text-wt-text-primary">Inventory Items Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Caption Options */}
                  <div className="space-y-4">
                    <Label className="text-wt-text-secondary">Caption Options</Label>
                    
                    <div className="flex items-center justify-between p-3 bg-wt-bg rounded-lg">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-wt-text-muted" />
                        <span className="text-sm text-wt-text-primary">Include Price</span>
                      </div>
                      <Switch
                        checked={autoPostSettings.include_price}
                        onCheckedChange={(checked) => setAutoPostSettings(prev => ({ ...prev, include_price: checked }))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-wt-bg rounded-lg">
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-wt-text-muted" />
                        <span className="text-sm text-wt-text-primary">Include Shop Link</span>
                      </div>
                      <Switch
                        checked={autoPostSettings.include_shop_link}
                        onCheckedChange={(checked) => setAutoPostSettings(prev => ({ ...prev, include_shop_link: checked }))}
                      />
                    </div>

                    {autoPostSettings.include_shop_link && (
                      <div className="space-y-2 pl-6">
                        <Label className="text-xs text-wt-text-muted">Shop Link URL (optional)</Label>
                        <Input
                          value={autoPostSettings.shop_link_url || ''}
                          onChange={(e) => setAutoPostSettings(prev => ({ ...prev, shop_link_url: e.target.value }))}
                          placeholder="https://yourshop.com"
                          className="bg-wt-bg border-wt-border text-wt-text-primary"
                        />
                        <p className="text-xs text-wt-text-muted">
                          If empty, will use product URL from Shopify
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Caption Template */}
                  <div className="space-y-2">
                    <Label className="text-wt-text-secondary">Caption Template</Label>
                    <Textarea
                      value={autoPostSettings.caption_template}
                      onChange={(e) => setAutoPostSettings(prev => ({ ...prev, caption_template: e.target.value }))}
                      placeholder="{title}\n\n{price}\n\nShop now: {link}"
                      className="bg-wt-bg border-wt-border text-wt-text-primary font-mono text-sm"
                    />
                    <p className="text-xs text-wt-text-muted">
                      Available variables: {'{title}'}, {'{price}'}, {'{link}'}
                    </p>
                  </div>

                  {/* Category Filter */}
                  {categories.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-wt-text-secondary">Category Filter</Label>
                        {autoPostSettings.category_filter?.length > 0 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setAutoPostSettings(prev => ({ ...prev, category_filter: [] }))}
                            className="text-xs text-wt-text-muted"
                          >
                            Clear All
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-wt-text-muted">
                        Select categories to auto-post. Leave empty to post all products.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {categories.map((category) => (
                          <Badge
                            key={category}
                            variant="outline"
                            className={`cursor-pointer transition-colors ${
                              autoPostSettings.category_filter?.includes(category)
                                ? 'bg-wt-gold/20 text-wt-gold border-wt-gold/50'
                                : 'bg-wt-bg border-wt-border text-wt-text-muted hover:text-wt-text-primary'
                            }`}
                            onClick={() => toggleCategory(category)}
                          >
                            <Tag className="h-3 w-3 mr-1" />
                            {category}
                            {autoPostSettings.category_filter?.includes(category) && (
                              <Check className="h-3 w-3 ml-1" />
                            )}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Save Button */}
                  <Button
                    onClick={handleSaveAutoPostSettings}
                    disabled={savingSettings}
                    className="w-full bg-wt-gold hover:bg-wt-gold-dark text-wt-bg"
                  >
                    {savingSettings ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Save Settings
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Post History Tab */}
          <TabsContent value="history">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-wt-surface border-wt-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-wt-text-primary">Post History</CardTitle>
                      <CardDescription className="text-wt-text-muted">
                        Recent posts made from this app
                      </CardDescription>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={loadData}
                      className="border-wt-border text-wt-text-secondary"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {posts.length === 0 ? (
                    <div className="text-center py-8">
                      <ImageIcon className="h-12 w-12 mx-auto text-wt-text-muted mb-4" />
                      <p className="text-wt-text-muted">No posts yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {posts.map((post) => (
                        <div key={post.id} className="flex gap-4 p-4 bg-wt-bg rounded-lg">
                          {/* Image Preview */}
                          <div className="flex-shrink-0">
                            {post.image_urls?.[0] && (
                              <img
                                src={post.image_urls[0]}
                                alt="Post"
                                className="w-16 h-16 object-cover rounded-lg"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect fill="%23333" width="64" height="64"/></svg>';
                                }}
                              />
                            )}
                          </div>
                          
                          {/* Post Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge 
                                variant="outline" 
                                className={post.auto_posted 
                                  ? 'bg-purple-500/20 text-purple-400 border-purple-500/30'
                                  : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                }
                              >
                                {post.auto_posted ? 'Auto-Posted' : 'Manual'}
                              </Badge>
                              <Badge variant="outline" className="bg-wt-surface border-wt-border text-wt-text-muted">
                                {post.media_type === 'carousel' ? `${post.image_urls?.length || 0} images` : 'Single image'}
                              </Badge>
                            </div>
                            <p className="text-sm text-wt-text-primary line-clamp-2">
                              {post.caption?.substring(0, 100)}
                              {post.caption?.length > 100 && '...'}
                            </p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-wt-text-muted">
                              <Clock className="h-3 w-3" />
                              {new Date(post.posted_at).toLocaleString()}
                              {post.product_title && (
                                <>
                                  <span>•</span>
                                  <span>{post.product_title}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default InstagramPostPage;
